/**
 * B631 leg (f) — tenant webhook endpoints, the management doors (CLASS A,
 * DEFAULT RECORDED 2026-10-06 by the design seat). An endpoint's signing
 * secret is handed over ONCE through Secrets Manager under the tenant's
 * prefix — the API key's custody — so neither the create response nor the
 * list carries anything that could sign or verify a delivery. The strict
 * responses below have no field a secret could ride.
 *
 * B706 (b) (CLASS A, DEFAULT RECORDED 2026-10-06; Pathik overrules with
 * "no on B706"): the doors ride their own permission, `webhook:write` —
 * ADMIN holds it, and so does an API key minted with `webhooks:manage`, the
 * one named exception to B631a ruling (iii). A key reaches only the
 * endpoints it created (the handler's fence: a foreign id is the uniform
 * 404), and since a key holds no Secrets Manager reader its create answers
 * the `whsec_` secret ONCE in `secret` (device.enroll's one-time precedent,
 * B20a) where a staff create answers the `handover` path. The create is no
 * longer admin-GATED (a gate admits humans only); the audience and the
 * permission keep it to ADMIN and the scoped key.
 * The event vocabulary mirrors @transact/domain's WEBHOOK_EVENTS (pinned
 * equal by the handlers' test).
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

const zWebhookEvent = z.enum(["order.confirmed", "order.cancelled", "ticket.redeemed"]);

const zWebhookEndpointSummary = z
  .object({
    endpointId: zId,
    name: z.string().min(1).max(60),
    url: z.string().min(1).max(2048),
    events: z.array(zWebhookEvent).min(1),
    mode: z.enum(["test", "live"]),
    /** The secret's last four — "whsec_…WxYz" in the list. Never the secret. */
    last4: z.string().min(4).max(4),
    createdAt: zEpochMs,
    createdBy: z.string().min(1),
    revokedAt: zEpochMs.optional(),
    revokedReason: z.string().min(1).optional(),
    status: z.enum(["ACTIVE", "REVOKED"]),
  })
  .strict();

const EXAMPLE_ENDPOINT = {
  endpointId: "wh8f3Hq2LmNp4RsT6",
  name: "Our CRM",
  url: "https://hooks.example.com/transact",
  events: ["order.confirmed", "ticket.redeemed"] as ("order.confirmed" | "ticket.redeemed")[],
  mode: "test" as const,
  last4: "WxYz",
  createdAt: 1_790_000_000_000,
  createdBy: "staff-1",
  status: "ACTIVE" as const,
};

/** The most live endpoints one business may hold — each event fans out to every one. */
export const WEBHOOK_ENDPOINTS_MAX = 10;

export const webhookCreate = writeOp({
  name: "webhook.create",
  permission: "webhook:write",
  principals: ["STAFF:ADMIN", "API_KEY"],
  fields: { name: z.string().min(1).max(200), url: z.string().min(1).max(4096), events: z.array(zWebhookEvent).min(1).max(3) },
  response: z
    .object({
      endpoint: zWebhookEndpointSummary,
      /** Staff: the Secrets Manager path the secret was handed to. */
      handover: z.string().min(1).optional(),
      /** API key (B706 b): the signing secret, this once — exactly one of the two rides (the handler's rule). */
      secret: z.string().regex(/^whsec_[A-Za-z0-9_-]{16,128}$/).optional(),
    })
    .strict(),
  exampleRequest: { name: "Our CRM", url: "https://hooks.example.com/transact", events: ["order.confirmed", "ticket.redeemed"] },
  exampleResponse: { endpoint: EXAMPLE_ENDPOINT, handover: "transact/dev/tenants/t-1/webhooks/wh8f3Hq2LmNp4RsT6" },
});

export const webhookRevoke = writeOp({
  name: "webhook.revoke",
  permission: "webhook:write",
  principals: ["STAFF:ADMIN", "API_KEY"],
  fields: { endpointId: zId, reason: z.string().min(1).max(200).optional() },
  response: z.object({ endpoint: zWebhookEndpointSummary }).strict(),
  exampleRequest: { endpointId: "wh8f3Hq2LmNp4RsT6", reason: "moved to a new server" },
  exampleResponse: { endpoint: { ...EXAMPLE_ENDPOINT, revokedAt: 1_790_000_100_000, revokedReason: "moved to a new server", status: "REVOKED" as const } },
});

export const webhookList = readOp({
  name: "webhook.list",
  permission: "webhook:write",
  principals: ["STAFF:ADMIN", "API_KEY"],
  fields: {},
  response: z.object({ endpoints: z.array(zWebhookEndpointSummary) }).strict(),
  exampleRequest: {},
  exampleResponse: { endpoints: [EXAMPLE_ENDPOINT] },
});
